import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { ViewSwitch } from '../components/ViewSwitch';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ShoppingBag, 
  Image, 
  FileText, 
  Loader2,
  ChevronRight
} from 'lucide-react';

export default function ShopifyPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [shopifyData, setShopifyData] = useState([]);
  const [shopifyImages, setShopifyImages] = useState([]);
  const [shopifySections, setShopifySections] = useState([]);
  const [winningDetails, setWinningDetails] = useState([]);
  const [winningImages, setWinningImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid');

  const fetchData = useCallback(async () => {
    try {
      const results = await Promise.all([
        supabase.from('products').select('ecom_id, url, platform, image_urls'),
        supabase.from('shopify').select('*'),
        supabase.from('shopify_product_images').select('*'),
        supabase.from('shopify_sections').select('*'),
        supabase.from('winning_product_details').select('*'),
        supabase.from('winning_product_images').select('*'),
      ]);

      setProducts(results[0].data || []);
      setShopifyData(results[1].data || []);
      setShopifyImages(results[2].data || []);
      setShopifySections(results[3].data || []);
      setWinningDetails(results[4].data || []);
      setWinningImages(results[5].data || []);
    } catch (error) {
      console.error('Error fetching Shopify data:', error);
      toast.error('Failed to load Shopify data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getRelatedData = (ecomId) => ({
    product: products.find(p => p.ecom_id === ecomId),
    shopifyImages: shopifyImages.find(si => si.ecom_id === ecomId),
    sections: shopifySections.find(ss => ss.ecom_id === ecomId),
    winning: winningDetails.find(w => w.ecom_id === ecomId),
    winningImages: winningImages.find(wi => wi.ecom_id === ecomId),
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="shopify-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1E1B4B] font-['Manrope']">
              Shopify Products
            </h1>
            <p className="text-slate-500 text-sm mt-1">{shopifyData.length} products synced</p>
          </div>
          <ViewSwitch view={view} onViewChange={setView} />
        </div>

        {shopifyData.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <ShoppingBag className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Shopify products</h3>
            <p className="text-slate-500">Products will appear here once synced to Shopify</p>
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {shopifyData.map((shopify) => {
              const data = getRelatedData(shopify.ecom_id);
              const heroImage = data.winningImages?.image1 || data.shopifyImages?.image1;
              const imageCount = [
                data.winningImages?.image1, data.winningImages?.image2, data.winningImages?.image3,
                data.winningImages?.image4, data.winningImages?.image5,
                data.shopifyImages?.image1, data.shopifyImages?.image2, data.shopifyImages?.image3,
                data.shopifyImages?.image4, data.shopifyImages?.image5, data.shopifyImages?.image6
              ].filter(Boolean).length;

              return (
                <div
                  key={shopify.id}
                  onClick={() => navigate(`/shopify/${shopify.ecom_id}`)}
                  className={cn(
                    'bg-white rounded-xl border border-slate-200 overflow-hidden',
                    'hover:shadow-lg hover:border-indigo-300 transition-all cursor-pointer group'
                  )}
                  data-testid={`shopify-card-${shopify.ecom_id}`}
                >
                  {/* Image */}
                  <div className="aspect-square bg-slate-100 overflow-hidden relative">
                    {heroImage ? (
                      <img src={heroImage} alt={shopify.product_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                    {shopify.shopify_product_id && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-0.5 rounded text-xs font-medium">
                        Synced
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <h3 className="font-semibold text-[#1E1B4B] text-sm line-clamp-2 mb-2">
                      {shopify.product_name || shopify.ecom_id}
                    </h3>
                    <StatusBadge status={shopify.status} lane="shopify" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            {shopifyData.map((shopify) => {
              const data = getRelatedData(shopify.ecom_id);
              const heroImage = data.winningImages?.image1 || data.shopifyImages?.image1;
              const imageCount = [
                data.winningImages?.image1, data.winningImages?.image2, data.winningImages?.image3,
                data.winningImages?.image4, data.winningImages?.image5,
                data.shopifyImages?.image1, data.shopifyImages?.image2, data.shopifyImages?.image3,
                data.shopifyImages?.image4, data.shopifyImages?.image5, data.shopifyImages?.image6
              ].filter(Boolean).length;

              return (
                <div
                  key={shopify.id}
                  onClick={() => navigate(`/shopify/${shopify.ecom_id}`)}
                  className={cn(
                    'bg-white rounded-xl border border-slate-200 overflow-hidden',
                    'hover:shadow-lg hover:border-indigo-300 transition-all cursor-pointer group'
                  )}
                  data-testid={`shopify-row-${shopify.ecom_id}`}
                >
                  <div className="flex items-center gap-4 p-4">
                    {/* Image */}
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0 relative">
                      {heroImage ? (
                        <img src={heroImage} alt={shopify.product_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="w-6 h-6 text-slate-300" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-[#1E1B4B] truncate text-sm md:text-base">
                        {shopify.product_name || shopify.ecom_id}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <StatusBadge status={shopify.status} lane="shopify" />
                        <span className="text-xs text-slate-500">{imageCount} images</span>
                        {shopify.shopify_product_id && (
                          <span className="text-xs text-green-600 font-medium">Synced</span>
                        )}
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-500 transition-colors flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
